/**
 * POS React Query hooks — query keys with tenant isolation.
 * Plan: 125-pos-react-ui, Epic 01 + Epic 02
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  keepPreviousData,
} from "@tanstack/react-query";
import { useAuthStore } from "@/lib/store/authStore";
import { posApi } from "./posApi";
import type {
  OpeningBalanceRow,
  POSItem,
  POSCollection,
  POSInvoiceDoc,
  POSSubmittedInvoice,
  POSClosingEntryDoc,
  POSClosingEntryResult,
  AvailableCoupon,
  POSTemplateVariantsResponse,
} from "../types";

function useTenant() {
  return useAuthStore((s) => s.currentTenant);
}

// G7: All query keys include tenant for cache isolation
export const posKeys = {
  all: (tenant: string | null) => ["pos", tenant] as const,
  openingEntry: (tenant: string | null, user: string) =>
    ["pos", "opening-entry", tenant, user] as const,
  profiles: (tenant: string | null, user: string) =>
    ["pos", "profiles", tenant, user] as const,
  profileData: (tenant: string | null, profile: string) =>
    ["pos", "profile-data", tenant, profile] as const,
  collections: (tenant: string | null, profile: string) =>
    ["pos", "collections", tenant, profile] as const,
  items: (
    tenant: string | null,
    profile: string,
    collection: string,
    search: string,
  ) => ["pos", "items", tenant, profile, collection, search] as const,
  taxTemplate: (tenant: string | null, template: string) =>
    ["pos", "tax-template", tenant, template] as const,
  customers: (tenant: string | null, search: string) =>
    ["pos", "customers", tenant, search] as const,
  pastOrders: (tenant: string | null, search: string, status: string) =>
    ["pos", "past-orders", tenant, search, status] as const,
  openingEntryDoc: (tenant: string | null, name: string) =>
    ["pos", "opening-entry-doc", tenant, name] as const,
  posInvoices: (
    tenant: string | null,
    start: string,
    end: string,
    profile: string,
    user: string,
  ) => ["pos", "pos-invoices", tenant, start, end, profile, user] as const,
  availableCoupons: (tenant: string | null, profile: string) =>
    ["pos", "available-coupons", tenant, profile] as const,
  templateVariants: (tenant: string | null, templateCode: string) =>
    ["pos", "template-variants", tenant, templateCode] as const,
};

/** Check if user has an open shift */
export function useCheckOpeningEntry(user: string | undefined) {
  const tenant = useTenant();
  return useQuery({
    queryKey: posKeys.openingEntry(tenant, user ?? ""),
    queryFn: () => posApi.checkOpeningEntry(user!),
    enabled: !!tenant && !!user,
  });
}

/** Get POS Profiles available to the current user */
export function useUserPOSProfiles(user: string | undefined) {
  const tenant = useTenant();
  return useQuery({
    queryKey: posKeys.profiles(tenant, user ?? ""),
    queryFn: () => posApi.getUserPOSProfiles(user!),
    enabled: !!tenant && !!user,
  });
}

/** Get full profile config (payment methods, item groups, etc.) */
export function usePOSProfileData(profileName: string | null) {
  const tenant = useTenant();
  return useQuery({
    queryKey: posKeys.profileData(tenant, profileName ?? ""),
    queryFn: () => posApi.getPOSProfileData(profileName!),
    enabled: !!tenant && !!profileName,
  });
}

/** Create POS Opening Entry (open shift) — mutation */
export function useCreateOpeningVoucher() {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      pos_profile: string;
      company: string;
      balance_details: OpeningBalanceRow[];
    }) =>
      posApi.createOpeningVoucher(
        params.pos_profile,
        params.company,
        params.balance_details,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: posKeys.all(tenant) });
    },
  });
}

// --- Epic 02 hooks ---

const POS_PAGE_SIZE = 20;

/** Fetch visible top-level Storefront Collections for POS category bar (Plan 132). */
export function usePOSCollections(posProfile: string | null) {
  const tenant = useTenant();
  return useQuery<POSCollection[], Error>({
    queryKey: posKeys.collections(tenant, posProfile ?? ""),
    queryFn: async () => {
      const res = await posApi.getPOSCollections(posProfile!);
      return res.collections;
    },
    enabled: !!tenant && !!posProfile,
    staleTime: 5 * 60_000, // collections rarely change during a shift
  });
}

/** Infinite-scroll product grid from get_items endpoint */
export function useGetItems(
  posProfile: string | null,
  priceList: string | null,
  collection: string,
  searchTerm: string,
) {
  const tenant = useTenant();
  return useInfiniteQuery<POSItem[], Error>({
    queryKey: posKeys.items(tenant, posProfile ?? "", collection, searchTerm),
    queryFn: async ({ pageParam = 0 }) => {
      const res = await posApi.getItems({
        start: pageParam as number,
        page_length: POS_PAGE_SIZE,
        price_list: priceList!,
        item_group: "", // always empty — collections replace item groups
        pos_profile: posProfile!,
        search_term: searchTerm || undefined,
        collection,
      });
      // G1: get_items returns { items: [...] }
      return res.items ?? [];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      (lastPage?.length ?? 0) < POS_PAGE_SIZE
        ? undefined
        : (lastPageParam as number) + POS_PAGE_SIZE,
    enabled: !!tenant && !!posProfile && !!priceList,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/** Fetch tax template for computing tax on cart totals */
export function useTaxTemplate(templateName: string | null) {
  const tenant = useTenant();
  return useQuery({
    queryKey: posKeys.taxTemplate(tenant, templateName ?? ""),
    queryFn: () => posApi.getTaxTemplate(templateName!),
    enabled: !!tenant && !!templateName,
    staleTime: 5 * 60_000, // tax templates rarely change
  });
}

// --- Epic 03 hooks ---

/** Barcode scan — mutation (triggered by scanner input, not a cached query) */
export function useSearchBarcode() {
  return useMutation({
    mutationFn: (searchValue: string) => posApi.searchBarcode(searchValue),
  });
}

// --- Epic 04 hooks ---

/**
 * Submit a POS Invoice — chains save (draft) → submit.
 * G14: Use mutation.isPending to disable button and prevent double-submit.
 */
export function useSubmitPOSInvoice() {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  return useMutation<POSSubmittedInvoice, Error, POSInvoiceDoc>({
    mutationFn: async (doc: POSInvoiceDoc) => {
      // Step 1: Save as draft
      const saved = await posApi.savePOSInvoice(doc);
      // Step 2: Submit (triggers GL entries, stock, loyalty)
      const submitted = await posApi.submitPOSInvoice(saved);
      return submitted;
    },
    onSuccess: () => {
      // Invalidate items (stock changed) and opening entry queries
      queryClient.invalidateQueries({ queryKey: posKeys.all(tenant) });
    },
  });
}

// --- Epic 05 hooks ---

/** Get past POS orders (searchable, filtered by status). G15: dedup client-side. */
export function useGetPastOrders(searchTerm: string, status: string) {
  const tenant = useTenant();
  return useQuery({
    queryKey: posKeys.pastOrders(tenant, searchTerm, status),
    queryFn: async () => {
      const orders = await posApi.getPastOrders(searchTerm, status);
      // G15: backend may return duplicates — dedup by invoice name
      const seen = new Set<string>();
      return orders.filter((o) => {
        if (seen.has(o.name)) return false;
        seen.add(o.name);
        return true;
      });
    },
    enabled: !!tenant && !!status,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });
}

/** Get full POS Opening Entry doc (for closing flow — needs user + balance_details). */
export function useGetOpeningEntryDoc(name: string | null) {
  const tenant = useTenant();
  return useQuery({
    queryKey: posKeys.openingEntryDoc(tenant, name ?? ""),
    queryFn: () => posApi.getOpeningEntryDoc(name!),
    enabled: !!tenant && !!name,
  });
}

/** Get POS Invoices for shift period (for closing entry reconciliation). */
export function useGetPOSInvoices(
  start: string | null,
  end: string | null,
  posProfile: string | null,
  user: string | null,
) {
  const tenant = useTenant();
  return useQuery({
    queryKey: posKeys.posInvoices(
      tenant,
      start ?? "",
      end ?? "",
      posProfile ?? "",
      user ?? "",
    ),
    queryFn: () =>
      posApi.getPOSInvoices(start!, end!, posProfile!, user!),
    enabled: !!tenant && !!start && !!end && !!posProfile && !!user,
  });
}

/** Create a return from a past POS Invoice. G16: returns unsaved doc. */
export function useMakeSalesReturn() {
  return useMutation({
    mutationFn: (sourceName: string) => posApi.makeSalesReturn(sourceName),
  });
}

/** Submit a POS Closing Entry — chains save → submit. G12: use isPending for double-submit guard. */
export function useSubmitClosingEntry() {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  return useMutation<POSClosingEntryResult, Error, POSClosingEntryDoc>({
    mutationFn: async (doc: POSClosingEntryDoc) => {
      const saved = await posApi.savePOSClosingEntry(doc);
      const submitted = await posApi.submitPOSClosingEntry(saved);
      return submitted;
    },
    onSuccess: () => {
      // G9: Invalidate all POS queries so gate page re-checks opening entry
      queryClient.invalidateQueries({ queryKey: posKeys.all(tenant) });
    },
  });
}

/** Search customers — enable only when search term is non-empty */
export function useSearchCustomers(searchTerm: string) {
  const tenant = useTenant();
  return useQuery({
    queryKey: posKeys.customers(tenant, searchTerm),
    queryFn: () => posApi.searchCustomers(searchTerm),
    enabled: !!tenant && searchTerm.length >= 2,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// --- Variant hooks (Plan 134, Epic 03) ---

/** Fetch variant data for a parent/template item (for variant selector modal). */
export function useTemplateVariants(
  templateItemCode: string | null,
  posProfile: string | null,
) {
  const tenant = useTenant();
  return useQuery<POSTemplateVariantsResponse, Error>({
    queryKey: posKeys.templateVariants(tenant, templateItemCode ?? ""),
    queryFn: () =>
      posApi.getTemplateVariants(templateItemCode!, posProfile!),
    enabled: !!tenant && !!templateItemCode && !!posProfile,
    staleTime: 5 * 60_000, // variant data rarely changes during a shift
  });
}

// --- Coupon hooks (Plan 133, Epic 02) ---

/** Validate a coupon code — mutation (triggered on Enter/select). */
export function useValidateCoupon() {
  return useMutation({
    mutationFn: (params: {
      pos_profile: string;
      coupon_code: string;
      customer?: string;
    }) =>
      posApi.validateCoupon(
        params.pos_profile,
        params.coupon_code,
        params.customer,
      ),
  });
}

/** Preview discount for a cart + coupon — mutation (triggered after validation). */
export function usePreviewDiscount() {
  return useMutation({
    mutationFn: (params: {
      pos_profile: string;
      coupon_code: string;
      items: { item_code: string; qty: number; rate: number }[];
      customer?: string;
    }) =>
      posApi.previewCouponDiscount(
        params.pos_profile,
        params.coupon_code,
        params.items,
        params.customer,
      ),
  });
}

/** List active coupons for the picker — fetched when picker opens. */
export function useAvailableCoupons(posProfile: string | null) {
  const tenant = useTenant();
  return useQuery<AvailableCoupon[], Error>({
    queryKey: posKeys.availableCoupons(tenant, posProfile ?? ""),
    queryFn: async () => {
      const res = await posApi.getAvailableCoupons(posProfile!);
      return res.coupons;
    },
    enabled: !!tenant && !!posProfile,
    staleTime: 60_000, // coupons don't change rapidly during a shift
  });
}
